import { DeadlineCountdown } from "@/components/deadline-countdown";

import { ExternalLink, FileCode, FileText, Loader2, Upload, Video } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";

import { Field } from "@/components/form-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAsync } from "@/hooks/use-async";
import type { Challenge, Report, ReportRun, ReportState } from "@/lib/api";
import { ApiError, studentApi } from "@/lib/api";
import { formatBytes, formatTimestamp } from "@/lib/format";
import {
  CODE_ACCEPT,
  DIFFICULTY_LABELS,
  downloadFile,
  formatDifficulty,
  formatRun,
  isCodeFile,
  openPdf,
  PROBLEMS_MAX,
} from "@/lib/reports";
import { cn } from "@/lib/utils";

/**
 * The team's report for this challenge: what is due, what is on record, and the form to
 * hand it in -- how hard the team found the challenge, the report slides as a PDF, and the
 * agent's code.
 *
 * Rendered only for a challenge that collects one, and only for a student of its course.
 * The server decides whether the caller may hand in now and says why not in `reason`;
 * this card shows that sentence rather than reimplementing the rules.
 */
export function ReportCard({ challenge, apiKey }: { challenge: Challenge; apiKey: string }) {
  const state = useAsync(() => studentApi.report(challenge.id, apiKey), [challenge.id, apiKey]);
  const [editing, setEditing] = useState(false);
  const data = state.data;

  return (
    <section
      id="report"
      className="bg-card text-card-foreground ring-foreground/10 scroll-mt-20 rounded-xl ring-1"
    >
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <span className="bg-primary/10 text-primary grid size-8 shrink-0 place-items-center rounded-lg">
          <FileText className="size-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-medium">Report</h2>
          <p className="text-muted-foreground text-xs">
            One per team: difficulty, slides as a PDF, and your agent&apos;s code.{" "}
            <DeadlineCountdown deadline={challenge.report_deadline} kind="report" />
          </p>
        </div>
        {data?.report ? (
          data.report.late ? (
            <Badge variant="destructive">Handed in late</Badge>
          ) : (
            <Badge variant="secondary">Handed in</Badge>
          )
        ) : data ? (
          <Badge variant="outline">Not handed in</Badge>
        ) : null}
      </div>

      <div className="grid gap-4 px-4 py-4">
        {challenge.report_template_url ? (
          <p className="text-sm">
            Fill in the{" "}
            <a
              href={challenge.report_template_url}
              target="_blank"
              rel="noreferrer"
              className="text-primary inline-flex items-center gap-0.5 underline-offset-4 hover:underline"
            >
              report template
              <ExternalLink className="size-3 opacity-60" aria-hidden />
            </a>{" "}
            (File &gt; Make a copy), then download it as a PDF (File &gt; Download &gt; PDF).
          </p>
        ) : null}

        {state.error ? (
          <p className="text-destructive text-sm">{state.error.message}</p>
        ) : !data ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : (
          <>
            {data.report ? (
              <OnRecord challengeId={challenge.id} apiKey={apiKey} report={data.report} />
            ) : null}

            {data.can_submit ? (
              data.report && !editing ? (
                <div>
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                    <Upload className="size-4" aria-hidden />
                    Replace
                  </Button>
                  <p className="text-muted-foreground mt-2 text-xs">
                    Any teammate can replace it until the deadline. After that, the version on
                    record is final.
                  </p>
                </div>
              ) : (
                <HandInForm
                  challengeId={challenge.id}
                  apiKey={apiKey}
                  current={data.report}
                  runs={data.runs}
                  onDone={(next) => {
                    setEditing(false);
                    state.refetch();
                    toast.success(next.report?.late ? "Handed in (late)" : "Handed in");
                  }}
                  onCancel={data.report ? () => setEditing(false) : undefined}
                />
              )
            ) : data.reason ? (
              <p className="text-muted-foreground text-sm">{data.reason}</p>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function OnRecord({
  challengeId,
  apiKey,
  report,
}: {
  challengeId: string;
  apiKey: string;
  report: Report;
}) {
  return (
    <div className="grid gap-2">
      <p className="text-sm">
        <span className="text-muted-foreground">Difficulty </span>
        <span className="font-medium">{formatDifficulty(report.difficulty)}</span>
      </p>
      {report.run ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Run to look at </span>
          <span className="font-medium">{formatRun(report.run)}</span>
        </p>
      ) : null}
      {report.problems ? (
        <p className="text-muted-foreground text-sm whitespace-pre-wrap">{report.problems}</p>
      ) : null}
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span>
          {report.team_name}: {report.members.map((m) => m.name || m.email).join(", ")}
        </span>
        <span>
          Handed in {formatTimestamp(report.submitted_at)}
          {report.submitted_by ? ` by ${report.submitted_by}` : ""}
          {report.revisions > 1 ? ` · version ${report.revisions}` : ""}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => openPdf(() => studentApi.reportFileUrl(challengeId, apiKey))}
        >
          <FileText className="size-4" aria-hidden />
          {report.file_name}
          <span className="text-muted-foreground">({formatBytes(report.file_size)})</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            downloadFile(() => studentApi.reportCodeUrl(challengeId, apiKey), report.code_file_name)
          }
        >
          <FileCode className="size-4" aria-hidden />
          {report.code_file_name}
          <span className="text-muted-foreground">({formatBytes(report.code_file_size)})</span>
        </Button>
        {report.repo_url ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(report.repo_url, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="size-4" aria-hidden />
            Repository
          </Button>
        ) : null}
        {report.video_url ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(report.video_url, "_blank", "noopener,noreferrer")}
          >
            <Video className="size-4" aria-hidden />
            Video
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function DifficultyPicker({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (value: number) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-labelledby="r-difficulty-label"
      className="grid grid-cols-5 gap-1.5"
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          onClick={() => onChange(n)}
          className={cn(
            "flex flex-col items-center gap-0.5 rounded-md border px-1 py-2 text-xs transition-colors",
            "focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]",
            value === n
              ? "border-primary bg-primary/10 text-foreground"
              : "text-muted-foreground hover:bg-muted",
          )}
        >
          <span className="text-base font-semibold">{n}</span>
          <span className="leading-tight">{DIFFICULTY_LABELS[n]}</span>
        </button>
      ))}
    </div>
  );
}

function HandInForm({
  challengeId,
  apiKey,
  current,
  runs,
  onDone,
  onCancel,
}: {
  challengeId: string;
  apiKey: string;
  current: Report | null;
  runs: ReportRun[];
  onDone: (state: ReportState) => void;
  onCancel?: () => void;
}) {
  const [difficulty, setDifficulty] = useState<number | null>(current?.difficulty ?? null);
  const [runId, setRunId] = useState(current?.run?.id ?? "");
  const [problems, setProblems] = useState(current?.problems ?? "");
  const [repoUrl, setRepoUrl] = useState(current?.repo_url ?? "");
  const [videoUrl, setVideoUrl] = useState(current?.video_url ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [code, setCode] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const problem =
    difficulty === null
      ? "Rate how difficult the challenge was for your team."
      : runs.length > 0 && !runId
        ? "Choose the run you want us to look at."
      : problems.length > PROBLEMS_MAX
        ? `At most ${PROBLEMS_MAX} characters on what failed.`
      : !current && !file
        ? "Attach the report PDF."
        : file && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")
          ? "The report must be a PDF."
          : !current && !code
            ? "Attach your code: my_agent.py, or a .zip."
            : code && !isCodeFile(code.name)
              ? "The code must be a .py file or a .zip."
              : repoUrl.trim() && !/^https?:\/\/\S+$/.test(repoUrl.trim())
                ? "The repository link must start with http:// or https://."
              : videoUrl.trim() && !/^https?:\/\/\S+$/.test(videoUrl.trim())
                ? "The video link must start with http:// or https://."
                : null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (problem || difficulty === null) return;
    setBusy(true);
    try {
      const next = await studentApi.handInReport(
        challengeId,
        {
          difficulty,
          session_id: runId,
          problems: problems.trim(),
          repo_url: repoUrl.trim(),
          video_url: videoUrl.trim(),
          file,
          code,
        },
        apiKey,
      );
      onDone(next);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Hand-in failed", {
        duration: 10000,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-4">
      <div className="grid gap-2">
        <p id="r-difficulty-label" className="text-sm font-medium">
          How difficult was the challenge for your team?{" "}
          <span className="text-destructive" aria-hidden>
            *
          </span>
        </p>
        <DifficultyPicker value={difficulty} onChange={setDifficulty} />
      </div>
      {runs.length > 0 ? (
        <Field
          id="r-run"
          label="Run to look at"
          required
          hint="Any of your team's runs on this challenge, including one that did not reach the goal."
        >
          <Select
            items={Object.fromEntries(runs.map((r) => [r.id, formatRun(r)]))}
            value={runId || null}
            onValueChange={(v) => {
              if (typeof v === "string") setRunId(v);
            }}
          >
            <SelectTrigger id="r-run" className="w-full">
              <SelectValue placeholder="Choose a run" />
            </SelectTrigger>
            <SelectContent>
              {runs.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {formatRun(r)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      ) : (
        <p className="text-muted-foreground text-xs">
          Your team has no finished run on this challenge yet. You can still hand in; once you
          have one, you will be asked which run to look at.
        </p>
      )}
      <Field
        id="r-problems"
        label="Did anything fail, and where?"
        hint="Installation, the API key, the exploration data, the session, the check-in. Optional."
      >
        <Textarea
          id="r-problems"
          rows={3}
          value={problems}
          maxLength={PROBLEMS_MAX}
          onChange={(e) => setProblems(e.target.value)}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="r-file"
          label="Report (PDF)"
          required={!current}
          hint={current ? "Leave empty to keep the PDF on record." : "Your slides as a PDF, up to 25 MB."}
        >
          <Input
            id="r-file"
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        <Field
          id="r-code"
          label="Code"
          required={!current}
          hint={
            current
              ? "Leave empty to keep the code on record."
              : "my_agent.py, or a .zip if it spans several files. No data or model weights."
          }
        >
          <Input
            id="r-code"
            type="file"
            accept={CODE_ACCEPT}
            onChange={(e) => setCode(e.target.files?.[0] ?? null)}
          />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="r-repo" label="Code repository" hint="A link, if you have one. Optional.">
          <Input
            id="r-repo"
            type="url"
            placeholder="https://github.com/…"
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
          />
        </Field>
        <Field id="r-video" label="Video link" hint="Optional.">
          <Input
            id="r-video"
            type="url"
            placeholder="https://"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
          />
        </Field>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className={cn("text-xs", problem ? "text-destructive" : "text-muted-foreground")}>
          {problem ?? "Handed in for your whole team. Any teammate can replace it before the deadline."}
        </p>
        <div className="flex gap-2">
          {onCancel ? (
            <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          ) : null}
          <Button type="submit" size="sm" disabled={Boolean(problem) || busy}>
            {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Upload className="size-4" aria-hidden />}
            {current ? "Replace report" : "Hand in"}
          </Button>
        </div>
      </div>
    </form>
  );
}
